// Commission accounts sagas.
//
// All reads, all using the takeLatest equivalent so a date-filter change cannot
// be overtaken by its own stale response.
//
// The balance watcher is hand-written because the response does not identify
// which agent it belongs to, so the guid is echoed back for the reducer to key
// on — the same pattern as the agent-exists check.

import { take, call, put, actionChannel } from 'redux-saga/effects';
import { buffers } from 'redux-saga';
import * as types from '../../lib/actionTypes.es6';
import * as actions from './actions.es6';
import {
  makeLatestWatcher,
  normalizeError
} from '../../components/CommissionsShared/makeWatcher.es6';
import {
  getCommissionAccountsForAgents,
  getAgentCommissionAccount,
  getCommissionAccountByGuid,
  getAgentCommissionAccountTransactions
} from '../../lib/requests.es6';

export var getCommissionAccountsRequestWatcher = makeLatestWatcher(
  types.GET_COMMISSION_ACCOUNTS_REQUEST,
  getCommissionAccountsForAgents,
  actions.getCommissionAccountsSuccess,
  actions.getCommissionAccountsFailed
);

export var getCommissionAccountByGuidRequestWatcher = makeLatestWatcher(
  types.GET_COMMISSION_ACCOUNT_BY_GUID_REQUEST,
  getCommissionAccountByGuid,
  actions.getCommissionAccountByGuidSuccess,
  actions.getCommissionAccountByGuidFailed,
  {
    args: function(payload) {
      return [payload && payload.guid ? payload.guid : payload];
    }
  }
);

// The endpoint wants the agent guid in the path AND in agent_guids; requests.es6
// fills in the redundant query param, so callers pass the guid once.
export var getAgentCommissionAccountTransactionsRequestWatcher =
  makeLatestWatcher(
    types.GET_AGENT_COMMISSION_ACCOUNT_TRANSACTIONS_REQUEST,
    getAgentCommissionAccountTransactions,
    actions.getAgentCommissionAccountTransactionsSuccess,
    actions.getAgentCommissionAccountTransactionsFailed,
    {
      args: function(payload) {
        var request = payload || {};
        var params = Object.assign({}, request);
        delete params.agentGuid;
        return [request.agentGuid, params];
      }
    }
  );

// Buffered for the same reason as makeWatcher: the batch builder asks for
// several agents' balances in one tick.
export function* getAgentCommissionAccountRequestWatcher() {
  var channel = yield actionChannel(
    types.GET_AGENT_COMMISSION_ACCOUNT_REQUEST,
    buffers.expanding()
  );
  for (;;) {
    var action = yield take(channel);
    var payload = action.payload || {};
    var agentGuid = payload.agentGuid || payload;
    try {
      var response = yield call(getAgentCommissionAccount, agentGuid);
      yield put(
        actions.getAgentCommissionAccountSuccess({
          agentGuid: agentGuid,
          response: response ? response.data : null
        })
      );
    } catch (e) {
      yield put(
        actions.getAgentCommissionAccountFailed({
          agentGuid: agentGuid,
          error: normalizeError(e)
        })
      );
    }
  }
}
